/**
 * ч.958 (рішення власника, зразок — скріни): «Облікові дані» облікового запису:
 * профіль (email — лише читання), зміна пароля, двофакторний вхід (Google
 * Authenticator), сповіщення в Telegram і «Мій підпис».
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { account, getMe } from '@/api/auth';
import { attachments } from '@/api/attachments';
import { useDefaults } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useToast } from '@/components/ToastProvider';
import { useConfirm } from '@/components/ConfirmProvider';
import { extractApiError } from '@/utils/extractApiError';
import { tenantRoleLabel } from '@/auth/roleLabels';
import { MODE_LABEL, USER_MODE_LOCKED, modeAvailable, useEffectiveMode, useModeContext, useSetAccountMode } from '@/hooks/useAccountMode';
import type { AccountMode } from '@/api/auth';


/** Картка розділу — біла плашка із заголовком і підзаголовком (як на скріні). */
function Card({ title, hint, badge, children }: {
  title: string; hint?: string; badge?: string; children: React.ReactNode;
}) {
  return (
    <div className="card">
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.05rem' }}>{title}</h2>
          {hint && <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{hint}</div>}
        </div>
        {badge && <span className="account__badge">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

export default function CredentialsSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const secQ = useQuery({ queryKey: ['account-security'], queryFn: account.security });
  const sec = secQ.data;
  // ч.1074: акаунти, де двофакторний вхід обовʼязковий. Власник такого акаунта
  // не вимикає його собі (бекенд відповість 409); решту попереджаємо.
  const reqBy = sec?.required_by ?? [];
  const ownerLock = reqBy.some((r) => r.is_owner);
  const reqNames = reqBy.map((r) => `«${r.tenant_name}»`).join(', ');
  const ownerLockTitle = 'Двофакторний вхід обовʼязковий в акаунті, яким ви володієте: '
    + 'спершу зніміть вимогу («Адміністрування акаунта» → «Безпека»)';
  async function confirmDrop(): Promise<boolean> {
    if (reqBy.length === 0) return true;
    return confirm({
      title: 'Вимкнути двофакторний вхід?',
      message: `В акаунтах ${reqNames} двофакторний вхід обовʼязковий — після вимкнення ви `
        + 'не зможете там працювати, доки не увімкнете його знову.',
      okLabel: 'вимкнути', cancelLabel: 'залишити',
    });
  }

  // --- режим облікового запису (ч.1080 → ч.1405: «ЕДО» / «Користувач» / «Бізнес») ---
  const modeCtx = useModeContext().data;
  const setModeMut = useSetAccountMode();
  const setMode = {
    isPending: setModeMut.isPending,
    mutate: (m: AccountMode) => setModeMut.mutate(m, {
      onSuccess: (ctx) => { if (ctx) toast.show({ type: 'success', message: 'Режим збережено' }); },
      onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
    }),
  };
  const effective = useEffectiveMode();
  const mode: AccountMode = effective ?? sec?.account_mode ?? 'EDO';
  const canUserMode = modeCtx?.can_user_mode ?? true;

  // --- пароль ---
  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const changePwd = useMutation({
    mutationFn: () => account.changePassword(curPwd, newPwd),
    onSuccess: () => {
      setCurPwd(''); setNewPwd(''); setNewPwd2('');
      toast.show({ type: 'success', message: 'Пароль змінено' });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  const pwdOk = curPwd.length > 0 && newPwd.length >= 8 && newPwd === newPwd2;
  // ч.1111 (власник): «нагадати пароль» поруч зі «змінити» — посилання на пошту
  // і в Telegram. Пароль у відкритому вигляді не зберігається — лише так.
  const remind = useMutation({
    mutationFn: () => account.remindPassword(),
    onSuccess: (d) => toast.show({
      type: 'success', durationMs: 9000,
      message: `Посилання для встановлення нового пароля надіслано на ${d.email}`
        + (d.telegram ? ' і в Telegram' : '') + ` (діє ${d.expires_in_minutes} хв)`,
    }),
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });

  // --- 2FA ---
  const [setup, setSetup] = useState<{ secret: string; uri: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [offPwd, setOffPwd] = useState('');
  // ч.1090: коли двофакторний вхід діє, зміни до нього (вимкнути, інший
  // застосунок, підключити/відключити Telegram) — лише з паролем.
  const tfaOn = !!sec && sec.two_factor_method !== 'NONE';
  const [secPwd, setSecPwd] = useState('');
  const needPwd = tfaOn && !secPwd;
  const needPwdTitle = needPwd ? 'Введіть пароль у блоці «Двофакторний вхід»' : undefined;
  const startSetup = useMutation({
    mutationFn: () => account.totpSetup(tfaOn ? secPwd : undefined),
    onSuccess: (d) => setSetup({ secret: d.secret, uri: d.provisioning_uri, qr: d.qr_data_uri }),
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  const enable2fa = useMutation({
    mutationFn: () => account.totpEnable(code.trim()),
    onSuccess: () => {
      setSetup(null); setCode('');
      qc.invalidateQueries({ queryKey: ['account-security'] });
      toast.show({ type: 'success', message: 'Двофакторний вхід увімкнено' });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  const setMethod = useMutation({
    mutationFn: (m: 'NONE' | 'TOTP' | 'TELEGRAM') =>
      account.setTwoFactorMethod(m, m === 'NONE' ? secPwd : undefined),
    onSuccess: () => {
      setSecPwd('');
      qc.invalidateQueries({ queryKey: ['account-security'] });
      toast.show({ type: 'success', message: 'Налаштування входу збережено' });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  // ч.960: «запамʼятати на 30 днів» — вибір юзера для наступного входу
  // (зберігаємо у браузері; сам токен пристрою видає сервер після коду).
  const [remember30, setRemember30] = useState(
    () => localStorage.getItem('auth.remember_device') !== '0');
  const toggleRemember = (v: boolean) => {
    setRemember30(v);
    localStorage.setItem('auth.remember_device', v ? '1' : '0');
  };
  const disable2fa = useMutation({
    mutationFn: () => account.totpDisable(offPwd),
    onSuccess: () => {
      setOffPwd('');
      qc.invalidateQueries({ queryKey: ['account-security'] });
      toast.show({ type: 'success', message: 'Двофакторний вхід вимкнено' });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });

  // --- Telegram (ч.959: підключення КНОПКОЮ, без ручного Chat ID) ---
  const [waiting, setWaiting] = useState(false);
  const linkTg = useMutation({
    mutationFn: () => account.telegramLink(tfaOn ? secPwd : undefined),
    onSuccess: (d) => {
      window.open(d.url, '_blank', 'noopener');
      setWaiting(true);
      toast.show({ type: 'info', durationMs: 8000,
        message: 'Відкрили бота — натисніть у ньому «Start», і сповіщення підключаться.' });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  // Поки чекаємо натискання Start — опитуємо статус, щоб сторінка оновилась сама.
  useEffect(() => {
    if (!waiting) return;
    const id = setInterval(() => {
      qc.invalidateQueries({ queryKey: ['account-security'] });
    }, 3000);
    const stop = setTimeout(() => setWaiting(false), 120000);
    return () => { clearInterval(id); clearTimeout(stop); };
  }, [waiting, qc]);
  useEffect(() => { if (sec?.telegram_chat_id) setWaiting(false); }, [sec?.telegram_chat_id]);
  const unlinkTg = useMutation({
    mutationFn: () => account.setTelegram(null, tfaOn ? secPwd : undefined),
    onSuccess: () => {
      setSecPwd('');
      qc.invalidateQueries({ queryKey: ['account-security'] });
      toast.show({ type: 'success', message: 'Сповіщення в Telegram вимкнено' });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  const testChat = useMutation({
    mutationFn: account.telegramTest,
    onSuccess: () => toast.show({ type: 'success', message: 'Тестове повідомлення надіслано' }),
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });

  // --- підпис (перенесено з попапа хідера, ч.958) ---
  // ч.1233: підпис належить користувачу і показується в документах УСІХ
  // доступних організацій (сервер віддає найсвіжіший). Організація тут — лише
  // місце зберігання файла: обрана в шапці, якщо в ній можна завантажувати
  // вкладення, інакше перша така.
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const attachOrgs = usePermissionsByOrg('documents.attachments.manage').availableOrgs;
  const orgId = attachOrgs.some((o) => o.id === defaultOrgId)
    ? defaultOrgId : (attachOrgs[0]?.id ?? '');
  const fileRef = useRef<HTMLInputElement>(null);
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const signQ = useQuery({
    queryKey: ['attachments', 'user.signature', meQ.data?.id, orgId],
    queryFn: () => attachments.list({
      organization_id: orgId, document_type: 'user.signature',
      document_id: meQ.data!.id,
    }),
    enabled: !!meQ.data?.id && !!orgId,
  });
  const currentSign = signQ.data?.[signQ.data.length - 1];
  useEffect(() => {
    let alive = true;
    let url: string | null = null;
    if (currentSign) {
      attachments.downloadBlob(currentSign.id).then((b) => {
        if (!alive) return;
        url = URL.createObjectURL(b);
        setSignUrl(url);
      });
    } else {
      setSignUrl(null);
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [currentSign?.id]);
  const uploadSign = useMutation({
    mutationFn: async (file: File) => {
      if (currentSign) await attachments.remove(currentSign.id);
      return attachments.upload({
        organization_id: orgId, document_type: 'user.signature',
        document_id: meQ.data!.id, file,
      });
    },
    // усі кеші підпису (у документах різних організацій ключ свій)
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', 'user.signature'] }),
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });

  const role = meQ.data?.tenant_role;
  return (
    <>
      <Card title="Профіль" hint="Email використовується для входу і не змінюється."
        badge={secQ.isLoading ? undefined : 'Профіль завантажено'}>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 4' }}><label>Email</label>
            <input value={sec?.email ?? ''} disabled /></div>
          <div className="field" style={{ gridColumn: 'span 4' }}><label>ПІБ</label>
            <input value={sec?.full_name ?? ''} disabled
              placeholder="не вказано" /></div>
          <div className="field" style={{ gridColumn: 'span 4' }}><label>Роль</label>
            <input value={tenantRoleLabel(role)} disabled /></div>
        </div>
      </Card>

      <Card title="Режим"
        hint="Як ви працюєте в сервісі. Змінити можна будь-коли; на права це не впливає.">
        <div className="seg-tabs" role="radiogroup" aria-label="Режим облікового запису">
          {(['EDO', 'USER', 'OWNER'] as AccountMode[]).map((k) => {
            const avail = modeCtx ? modeAvailable(modeCtx, k) : { ok: true as const };
            const locked = !avail.ok;
            return (
              <button key={k} type="button" role="radio" aria-checked={mode === k}
                className={mode === k ? 'active' : ''} disabled={setMode.isPending || locked}
                title={locked ? avail.why : undefined}
                onClick={() => { if (mode !== k) setMode.mutate(k); }}>{MODE_LABEL[k]}</button>
            );
          })}
        </div>
        <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.4rem' }}>
          {mode === 'EDO'
            ? '«ЕДО» — лише електронні документи у вашому акаунті: картки учасника ЕДО (фізособа, ФОП, юрособа) без обмеження кількості; безкоштовно 20 документів на всі картки разом, далі сховище 50 МБ за 50 грн на 30 днів за кожну картку.'
            : mode === 'USER'
              ? '«Користувач» — працюєте в акаунтах, куди вас запросили: у шапці обираєте лише чужі акаунти, ваш особистий там не показується.'
              : '«Бізнес» — ведете власний бізнес у своєму акаунті: повне меню сервісу, організацію створюєте в розділі «Організації»; вибору акаунта в шапці немає — лише організація і період.'}
          {!canUserMode && mode !== 'USER' && <> {USER_MODE_LOCKED}.</>}
        </div>
      </Card>

      <Card title="Пароль" hint="Мінімум 8 символів. Після зміни на інших пристроях доведеться увійти знову. Не памʼятаєте поточний — «нагадати пароль»: надішлемо посилання для нового пароля на пошту і в Telegram.">
        {/* ч.1072 (рішення власника): три поля і кнопка — одним рядком. */}
        <div className="account__pwd-row">
          <div className="field"><label>Поточний пароль</label>
            <input type="password" value={curPwd} autoComplete="current-password"
              onChange={(e) => setCurPwd(e.target.value)} /></div>
          <div className="field"><label>Новий пароль</label>
            <input type="password" value={newPwd} autoComplete="new-password"
              onChange={(e) => setNewPwd(e.target.value)} /></div>
          <div className="field"><label>Новий пароль ще раз</label>
            <input type="password" value={newPwd2} autoComplete="new-password"
              onChange={(e) => setNewPwd2(e.target.value)} /></div>
          <button className="primary" disabled={!pwdOk || changePwd.isPending}
            onClick={() => changePwd.mutate()}>змінити пароль</button>
          <button type="button" disabled={remind.isPending}
            title="Надішлемо одноразове посилання для встановлення нового пароля на вашу пошту і в Telegram (якщо підключено)"
            onClick={() => remind.mutate()}>{remind.isPending ? 'надсилаємо…' : 'нагадати пароль'}</button>
        </div>
        {newPwd && newPwd.length < 8 && (
          <div style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem', marginTop: '0.35rem' }}>Пароль закороткий — мінімум 8 символів.</div>
        )}
        {newPwd2 && newPwd !== newPwd2 && (
          <div style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem', marginTop: '0.35rem' }}>Паролі не збігаються.</div>
        )}

        {/* ч.960 (рішення власника): двофакторний вхід — галка і вибір каналу
            просто у блоці пароля. Неготовий канал — неактивний з підказкою.
            ч.1072: окремою помітною плашкою з позначкою «рекомендовано»,
            щоб не губилась; підказка «як увімкнути» — текстом, не лише title. */}
        <div className={`account__tfa${sec?.two_factor_method !== 'NONE' ? ' is-on' : ''}`}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" style={{ width: 'auto' }}
              checked={sec?.two_factor_method !== 'NONE'}
              // ч.1129: «обовʼязковий» забороняє ВИМКНУТИ, а не увімкнути. Раніше
              // власнику платформи галка була неактивна завжди — з одним лише
              // Telegram (без застосунку) увімкнути вхід тут було нічим.
              disabled={(!sec?.totp_ready && !sec?.telegram_ready)
                || ((sec?.two_factor_required || ownerLock) && tfaOn)}
              title={(sec?.two_factor_required && tfaOn)
                ? 'Для власника платформи двофакторний вхід обовʼязковий — вимкнути його не можна'
                : (ownerLock && tfaOn) ? ownerLockTitle
                : (!sec?.totp_ready && !sec?.telegram_ready)
                  ? 'Спершу налаштуйте Google Authenticator або підключіть Telegram'
                  : undefined}
              onChange={(e) => {
                if (e.target.checked) {
                  setMethod.mutate(sec?.totp_ready ? 'TOTP' : 'TELEGRAM');
                } else if (!secPwd) {
                  toast.show({ type: 'warning', message: 'Щоб вимкнути двофакторний вхід, введіть пароль нижче' });
                } else {
                  void confirmDrop().then((ok) => { if (ok) setMethod.mutate('NONE'); });
                }
              }} />
            <b>Двофакторний вхід<sup className="account__recommend">рекомендовано</sup></b>
            <span style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
              — окрім пароля питаємо одноразовий код
            </span>
          </label>
          {reqBy.length > 0 && (
            <div className="account__tfa-hint">
              Обовʼязковий в акаунтах: {reqNames}
              {ownerLock ? ' — ви власник, тож вимкнути його не можна, доки діє вимога.' : '.'}
            </div>
          )}
          {sec && !sec.totp_ready && !sec.telegram_ready && (
            <div className="account__tfa-hint">
              Щоб увімкнути, налаштуйте нижче Google Authenticator або підключіть Telegram.
            </div>
          )}
          {sec && !tfaOn && (sec.totp_ready || sec.telegram_ready) && (
            <div className="account__tfa-hint">
              {sec.two_factor_required
                ? 'Для власника платформи двофакторний вхід обовʼязковий — поставте галочку, щоб увімкнути його.'
                : 'Канал для коду готовий — поставте галочку, щоб увімкнути двофакторний вхід.'}
            </div>
          )}
          {tfaOn && (
            <div className="field" style={{ maxWidth: 360, margin: '0.5rem 0 0 1.6rem' }}>
              <label htmlFor="sec-pwd">Пароль для змін двофакторного входу</label>
              <input id="sec-pwd" type="password" value={secPwd} autoComplete="current-password"
                placeholder="потрібен, щоб вимкнути або змінити спосіб входу"
                onChange={(e) => setSecPwd(e.target.value)} />
              <div className="account__tfa-hint" style={{ marginLeft: 0 }}>
                Втратили телефон із застосунком або Telegram?{' '}
                <Link to="/help/account-recovery-2fa">Як відновити вхід</Link>
              </div>
            </div>
          )}

          {sec?.two_factor_method !== 'NONE' && (
            <div style={{ paddingLeft: '1.6rem', marginTop: '0.5rem',
              display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                opacity: sec?.totp_ready ? 1 : 0.5 }}>
                <input type="radio" name="tfa-method" style={{ width: 'auto' }}
                  checked={sec?.two_factor_method === 'TOTP'}
                  disabled={!sec?.totp_ready}
                  title={sec?.totp_ready ? undefined : 'Спершу налаштуйте застосунок нижче'}
                  onChange={() => setMethod.mutate('TOTP')} />
                Google Authenticator
                {!sec?.totp_ready && (
                  <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
                    (не налаштовано)
                  </span>
                )}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                opacity: sec?.telegram_ready ? 1 : 0.5 }}>
                <input type="radio" name="tfa-method" style={{ width: 'auto' }}
                  checked={sec?.two_factor_method === 'TELEGRAM'}
                  disabled={!sec?.telegram_ready}
                  title={sec?.telegram_ready ? undefined : 'Спершу підключіть Telegram нижче'}
                  onChange={() => setMethod.mutate('TELEGRAM')} />
                Код у Telegram
                {!sec?.telegram_ready && (
                  <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
                    (не підключено)
                  </span>
                )}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                marginTop: '0.2rem' }}>
                <input type="checkbox" style={{ width: 'auto' }}
                  checked={remember30}
                  onChange={(e) => toggleRemember(e.target.checked)} />
                Запамʼятати на 30 днів
                <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
                  — на цьому пристрої код питатимемо рідше
                </span>
              </label>
            </div>
          )}
        </div>
      </Card>

      <Card title="Google Authenticator"
        hint="Застосунок-аутентифікатор: одноразовий код кожні 30 секунд."
        badge={sec?.totp_ready ? 'Налаштовано' : 'Не налаштовано'}>
        {sec?.totp_ready ? (
          <>
            <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Застосунок підключено
              {sec.two_factor_method === 'TOTP' && ' і використовується для входу'}.
              {sec.two_factor_required && ' Для власника платформи вимкнути не можна.'}
            </div>
            {!sec.two_factor_required && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', maxWidth: 520 }}>
                <div className="field" style={{ flex: 1, margin: 0 }}>
                  <label>Пароль для підтвердження</label>
                  <input type="password" value={offPwd}
                    onChange={(e) => setOffPwd(e.target.value)} />
                </div>
                <button className="danger"
                  disabled={!offPwd || disable2fa.isPending
                    || (ownerLock && sec.two_factor_method === 'TOTP')}
                  title={ownerLock && sec.two_factor_method === 'TOTP' ? ownerLockTitle : undefined}
                  onClick={() => {
                    const drops = sec.two_factor_method === 'TOTP';
                    void (drops ? confirmDrop() : Promise.resolve(true))
                      .then((ok) => { if (ok) disable2fa.mutate(); });
                  }}>вимкнути</button>
              </div>
            )}
          </>
        ) : setup ? (
          <>
            {/* ч.960 (рішення власника): QR — щоб не вводити ключ руками. */}
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap',
              alignItems: 'flex-start', marginBottom: '0.6rem' }}>
              {setup.qr && (
                <img src={setup.qr} alt="QR-код для Google Authenticator"
                  style={{ width: 170, height: 170, border: '1px solid var(--border-soft, #e2e8f0)',
                    borderRadius: 8, background: 'var(--bg-panel)' }} />
              )}
              <ol style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
                <li>Відкрийте Google Authenticator → «Додати» → <b>«Сканувати QR-код»</b>.</li>
                <li>Наведіть камеру на код ліворуч.</li>
                <li>Введіть 6-значний код із застосунку нижче.</li>
                <li style={{ color: 'var(--muted)' }}>
                  Без камери — «Ввести ключ» вручну:{' '}
                  <code style={{ fontWeight: 700 }}>{setup.secret}</code>
                </li>
              </ol>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', maxWidth: 420 }}>
              <div className="field" style={{ flex: 1, margin: 0 }}>
                <label>Код із застосунку</label>
                <input value={code} inputMode="numeric" maxLength={6}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
              </div>
              <button className="primary" disabled={code.length !== 6 || enable2fa.isPending}
                onClick={() => enable2fa.mutate()}>підтвердити</button>
              <button onClick={() => { setSetup(null); setCode(''); }}>скасувати</button>
            </div>
          </>
        ) : (
          <button className="primary" disabled={startSetup.isPending || needPwd}
            title={needPwdTitle}
            onClick={() => startSetup.mutate()}>налаштувати двофакторний вхід</button>
        )}
      </Card>

      <Card title="Сповіщення в Telegram"
        hint="Отримайте Chat ID через Telegram-бота і надішліть тестове повідомлення."
        badge={sec?.telegram_chat_id ? 'Chat ID збережено' : undefined}>
        {!sec?.telegram_bot_configured && (
          <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid #fbbf24', borderRadius: 6,
            padding: '0.5rem 0.7rem', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
            Бота ще не підключено на сервері — Chat ID збережеться, але повідомлення
            почнуть надходити після налаштування адміністратором.
          </div>
        )}
        {sec?.telegram_chat_id ? (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.9rem' }}>
              Сповіщення надходять у ваш Telegram
              {sec.telegram_bot_username
                ? <> (бот <b>@{sec.telegram_bot_username.replace('@', '')}</b>)</> : null}.
            </span>
            <button style={{ marginLeft: 'auto' }} disabled={testChat.isPending}
              onClick={() => testChat.mutate()}>тестове повідомлення</button>
            <button className="danger"
              disabled={unlinkTg.isPending || needPwd
                || (ownerLock && sec.two_factor_method === 'TELEGRAM')}
              title={ownerLock && sec.two_factor_method === 'TELEGRAM' ? ownerLockTitle : needPwdTitle}
              onClick={() => {
                const drops = sec.two_factor_method === 'TELEGRAM';
                void (drops ? confirmDrop() : Promise.resolve(true))
                  .then((ok) => { if (ok) unlinkTg.mutate(); });
              }}>відключити</button>
          </div>
        ) : (
          <>
            <div style={{ background: 'var(--bg-soft, #f8fafc)', border: '1px solid var(--border-soft, #e2e8f0)',
              borderRadius: 8, padding: '0.7rem 0.9rem', fontSize: '0.85rem', marginBottom: '0.7rem' }}>
              Натисніть «підключити Telegram» — відкриється наш бот
              {sec?.telegram_bot_username
                ? <> <b>@{sec.telegram_bot_username.replace('@', '')}</b></> : null}.
              У боті натисніть <b>Start</b> — і все підключиться саме,
              нічого копіювати не треба.
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="primary"
                disabled={linkTg.isPending || !sec?.telegram_bot_configured || needPwd}
                title={!sec?.telegram_bot_configured ? 'Бота ще не налаштовано на сервері' : needPwdTitle}
                onClick={() => linkTg.mutate()}>підключити Telegram</button>
              {waiting && (
                <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                  Чекаємо натискання «Start» у боті…
                </span>
              )}
            </div>
          </>
        )}
      </Card>

      <Card title="Мій підпис"
        hint="PDF до 2 МБ. Накладається на документи всіх ваших організацій кнопкою «+ мій підпис».">
        {!orgId && (
          <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            Немає організації, у якій вам дозволено завантажувати файли, — підпис нікуди зберегти.
          </div>
        )}
        <div style={{
          height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'repeating-conic-gradient(#f1f5f9 0% 25%, #fff 0% 50%) 0 0 / 14px 14px',
          borderRadius: 6, marginBottom: '0.5rem', maxWidth: 420,
        }}>
          {signUrl
            ? <img src={signUrl} alt="підпис" style={{ maxHeight: 84, maxWidth: '100%' }} />
            : <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>не завантажено</span>}
        </div>
        <button disabled={uploadSign.isPending || !orgId}
          onClick={() => fileRef.current?.click()}>
          {uploadSign.isPending ? 'завантаження…'
            : currentSign ? 'замінити підпис' : 'завантажити підпис (PDF)'}
        </button>
        <input ref={fileRef} type="file" hidden accept=".pdf,application/pdf"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadSign.mutate(f);
            e.target.value = '';
          }} />
      </Card>
    </>
  );
}
